import { makeStyles } from '@material-ui/styles'
import clsx from 'clsx'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { allKey } from 'app/constants'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import Progress from 'core/components/progress/Progress'
import SearchBar from 'core/components/SearchBar'
import Button from 'core/elements/button/Button'
import Divider from 'core/elements/Divider'
import DropdownToggle from 'core/elements/dropdown/DropdownToggle'
import Checkbox from 'core/elements/input/Checkbox'
import Text from 'core/elements/Text'
import Theme from 'core/themes/model'

export interface Item {
  label: string
  value: string
}

interface Props {
  label?: string
  placeholder?: string
  type?: 'checkbox' | 'radio'
  items: Item[]
  loading?: boolean
  onChange?: (selectedItem: Item | Item[]) => void
  handleApplyFilter?: (selectedItems: Item | Item[]) => void
  width?: number
  data?: any[]
  showLabel?: boolean
  align?: 'left' | 'right'
  value?: any
  icon?: string
  disabled?: boolean
  menuHeaderLabel?: string
}

const RadioDropdownFilter = ({
  label = '',
  placeholder,
  type = 'checkbox',
  items,
  loading,
  onChange,
  handleApplyFilter,
  width,
  data = [],
  showLabel = true,
  align = 'right',
  value = '',
  icon,
  disabled = false,
  menuHeaderLabel = 'Filter by',
}: Props) => {
  const isCheckboxType = type === 'checkbox'
  const isRadioType = type === 'radio'
  const valueItem = { label: value, value: value }
  const allItem: Item = useMemo(
    () => ({ label: placeholder ? placeholder : `All ${label}s`, value: allKey }),
    [label, placeholder],
  )
  const classes = useStyles()
  const [selectedItems, setSelectedItems] = useState<Item[] | Item>(
    isCheckboxType ? [...items] : valueItem,
  )
  const [searchTerm, setSearchTerm] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const prevItemsRef = useRef<Item[]>(items)

  // Store the previous selection to restore when dropdown is closed without applying
  const [previousSelection, setPreviousSelection] = useState<Item[] | Item>(
    isCheckboxType ? [...items] : valueItem,
  )

  const filteredItems = useMemo(
    () =>
      items?.filter((item) => item.label.toLowerCase().includes(searchTerm.toLowerCase())) || [],
    [items, searchTerm],
  )

  // Apply filter changes whenever data changes
  useEffect(() => {
    // Add loading check for race condition where data &
    // handleApplyFilter are loaded in before items are set,
    // and filter is not reapplied after items are set
    if (!loading) {
      handleApplyFilter && handleApplyFilter(previousSelection)
    }
  }, [data, handleApplyFilter, loading])

  useEffect(() => {
    if (onChange) {
      onChange(selectedItems)
    }
  }, [selectedItems, onChange])

  useEffect(() => {
    // Only update selected items if items array length changes or when mounted
    const prevItems = prevItemsRef.current
    const itemsChanged =
      prevItems.length !== items.length ||
      items.some((item) => !prevItems.some((prevItem) => prevItem.value === item.value))

    if (itemsChanged) {
      const newSelection = isCheckboxType ? [...items] : valueItem
      setSelectedItems(newSelection)
      setPreviousSelection(newSelection)
      prevItemsRef.current = items
    }
  }, [items, type, valueItem])

  const dropdownRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
        // Restore previous selection when clicking outside
        isCheckboxType && setSelectedItems(previousSelection)
        setSearchTerm('')
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [previousSelection])

  const handleSelection = useCallback(
    (item: Item) => {
      if (isRadioType) {
        setSelectedItems(item)
        setIsOpen(false)
      } else {
        setSelectedItems((prev: Item[] | Item) => {
          const prevArray = Array.isArray(prev) ? prev : [prev]
          return prevArray.some((selected) => selected.value === item.value)
            ? prevArray.filter((selected) => selected.value !== item.value)
            : [...prevArray, item]
        })
      }
    },
    [type],
  )

  const handleSelectAllChange = useCallback(() => {
    // If all items are selected, clear selection
    if (Array.isArray(selectedItems) && selectedItems?.length === items?.length) {
      setSelectedItems([])
      return
    }

    // If no items are selected, select all
    setSelectedItems(items)
  }, [type, items, selectedItems])

  const toggleDropdown = useCallback(() => {
    if (disabled) return

    if (!isOpen) {
      // When opening dropdown, store current selection as previous selection
      setPreviousSelection(selectedItems)
    }
    setIsOpen((prev) => !prev)
  }, [isOpen, selectedItems])

  const closeDropdown = useCallback(() => {
    // Restore previous selection when closing
    setSelectedItems(previousSelection)
    setIsOpen(false)
    setSearchTerm('')
  }, [previousSelection])

  const handleApplyFilterClick = useCallback(() => {
    // Update previous selection to current selection when applying filter
    setPreviousSelection(selectedItems)
    handleApplyFilter(selectedItems)
    setSearchTerm('')
    setIsOpen(false)
  }, [handleApplyFilter, selectedItems])

  const displayText = useMemo(() => {
    if (isRadioType) {
      return (selectedItems as Item).label
    }

    if (Array.isArray(selectedItems)) {
      if (selectedItems.length === items?.length) {
        return allItem.label
      }
      if (selectedItems.length > 0) {
        return selectedItems.map((item) => item.label).join(', ')
      }
    }

    return `Select ${label}`
  }, [type, selectedItems, items, allItem, label])

  const isAllSelected = useMemo(() => {
    if (isRadioType) {
      return (selectedItems as Item).value === allItem.value
    }
    return Array.isArray(selectedItems) && selectedItems.length === items?.length
  }, [type, selectedItems, allItem, items])

  return (
    <div className={classes.dropdownContainer} ref={dropdownRef}>
      <div className={classes.selectContainer} onClick={toggleDropdown}>
        {showLabel && <Text variant="caption1">{label}</Text>}
        <Progress loading={loading} inline>
          <div
            className={clsx(
              classes.inputFrame,
              disabled && classes.inputFrameDisabled,
              isOpen && classes.expandedInputFrame,
            )}
          >
            <div className={classes.selectContainer}>
              {icon && <FontAwesomeIcon>{icon}</FontAwesomeIcon>}
              <div className={clsx(classes.placeholder, 'placeholder')}>
                <Text variant="caption1">{displayText}</Text>
              </div>
            </div>
            <DropdownToggle
              type="button"
              disabled={disabled}
              className={clsx(classes.dropdownToggle, disabled && classes.dropdownToggleDisabled)}
            >
              <FontAwesomeIcon solid size="sm" disabled={disabled}>
                {isOpen ? 'caret-up' : 'caret-down'}
              </FontAwesomeIcon>
            </DropdownToggle>
          </div>
        </Progress>
      </div>
      {isOpen && (
        <div
          className={clsx(
            classes.dropdownMenu,
            width && classes.dropdownMenuCustomWidth,
            align === 'right' && classes.dropdownMenuAlignRight,
            align === 'left' && classes.dropdownMenuAlignLeft,
          )}
          style={width ? { width } : undefined}
        >
          <Text variant="subtitle2" className={classes.menuHeader}>
            {menuHeaderLabel}{' '}
            {label && (
              <Text variant="subtitle2" component="span">
                ({label})
              </Text>
            )}
          </Text>
          <div className={classes.radioContainer}>
            {items.length ? (
              <>
                {isCheckboxType && (
                  <label
                    className={classes.radioLabel}
                    data-testid="radio-dropdown-filter--label-0"
                  >
                    <Checkbox
                      checked={isAllSelected}
                      type={type}
                      onChange={handleSelectAllChange}
                    />
                    <Text variant="caption1">{allItem.label}</Text>
                  </label>
                )}
                {isCheckboxType && <Divider className={classes.divider} />}
                <SearchBar
                  className={classes.searchBar}
                  searchTerm={searchTerm}
                  onSearchChange={setSearchTerm}
                />
                <div className={classes.radioLabelContainer}>
                  {filteredItems?.map((item) => (
                    <label
                      key={item?.value}
                      className={classes.radioLabel}
                      data-testid="radio-dropdown-filter--label-1"
                    >
                      <Checkbox
                        checked={
                          isRadioType
                            ? (selectedItems as Item)?.value === item?.value
                            : Array.isArray(selectedItems) &&
                              selectedItems?.some((selected) => selected?.value === item?.value)
                        }
                        type={type}
                        onChange={() => handleSelection(item)}
                      />
                      <Text variant="caption1">{item?.label}</Text>
                    </label>
                  ))}
                </div>
              </>
            ) : (
              <Text variant="body2">No data found</Text>
            )}
          </div>
          {isCheckboxType && (
            <div className={classes.buttonContainer}>
              <Button variant="secondary" onClick={closeDropdown}>
                Cancel
              </Button>
              {!!items?.length && (
                <Button
                  variant="primary"
                  onClick={handleApplyFilterClick}
                  disabled={!filteredItems?.length}
                >
                  Apply Filter
                </Button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default RadioDropdownFilter

interface StyleProps {
  width?: number
  align?: 'left' | 'right'
  disabled?: boolean
}

const useStyles = makeStyles<Theme>((theme) => ({
  dropdownMenu: {
    width: 348,
    border: `1px solid ${theme.palette.grey[200]}`,
    borderRadius: '4px',
    backgroundColor: '#fff',
    boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
    position: 'absolute',
    zIndex: 20,
    right: 'auto',
    left: 'auto',
  },
  dropdownMenuCustomWidth: {
    width: 'auto',
  },
  dropdownMenuAlignRight: {
    right: 0,
    left: 'auto',
  },
  dropdownMenuAlignLeft: {
    right: 'auto',
    left: 0,
  },
  radioContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    padding: theme.spacing(2),
  },
  radioLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    cursor: 'pointer',
    padding: theme.spacing(1),
    border: `1px solid ${theme.palette.grey[200]}`,
    borderRadius: '4px',
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'end',
    gap: theme.spacing(2),
    padding: theme.spacing(2, 3, 2, 0),
    borderTop: `1px solid ${theme.palette.grey[200]}`,
  },
  button: {
    padding: theme.spacing(1, 2),
    borderRadius: '4px',
    border: 'none',
    cursor: 'pointer',
    fontSize: theme.typography.body2.fontSize,
    fontWeight: 'bold',
    backgroundColor: '#ccc',
    color: '#000',
    '&:hover': {
      backgroundColor: '#b0b0b0',
    },
  },
  primaryButton: {
    backgroundColor: '#3f51b5',
    color: '#fff',
    '&:hover': {
      backgroundColor: '#303f9f',
    },
  },
  menuHeader: {
    padding: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.grey[200]}`,
    '& span': {
      fontWeight: 'normal',
    },
  },
  divider: {
    margin: theme.spacing(1, 0),
    background: theme.palette.grey[200],
  },
  searchBar: {
    gap: 0,
    marginBottom: theme.spacing(1),
  },
  radioLabelContainer: {
    display: 'grid',
    gap: theme.spacing(1),
    overflowY: 'auto',
    maxHeight: '200px',
  },
  dropdownContainer: {
    position: 'relative',
  },
  inputFrame: {
    ...theme.typography.inputPlaceholder,
    marginTop: 1,
    position: 'relative',
    paddingRight: 26,
    backgroundColor: theme.components.input.frame.background,
    color: theme.components.input.frame.color,
    borderRadius: 4,
    borderColor: theme.components.input.frame.border,
    borderWidth: 1,
    borderStyle: 'solid',
    padding: theme.spacing(0, 1),
    width: '200px',
    cursor: 'pointer',
    opacity: 1,
  },
  inputFrameDisabled: {
    cursor: 'not-allowed',
    opacity: 0.5,
  },
  expandedInputFrame: {
    borderBottomRightRadius: 0,
    borderBottomLeftRadius: 0,
  },
  placeholder: {
    fontWeight: 400,
    background: 'transparent',
    minHeight: 36,
    lineHeight: 2.4,
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
  },
  selectContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  dropdownToggle: {
    cursor: 'pointer',
    '& i': {
      cursor: 'pointer',
    },
  },
  dropdownToggleDisabled: {
    cursor: 'not-allowed',
    '& i': {
      cursor: 'not-allowed',
    },
  },
}))
